/**
 * Hooks de Gestion y Auditoria de Configuracion (ADR_011).
 *
 * Reusan el patron de useTelegram/useEcc: react-query con invalidacion
 * automatica de cache en las mutations.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import type {
  BiometricosResponse,
  CamarasConfigResponse,
  CamarasResponse,
  GrabadoresAxeliaResponse,
  GrabadoresResponse,
  PanelesResponse,
  HikCentralResponse,
  InventarioLanResponse,
  ConfigDevice,
  ConfigDeviceConfig,
  ConfigDeviceConfigList,
  ConfigDeviceFilters,
  ConfigDeviceList,
  ConfigDiffResponse,
  ConfigDriftFilters,
  ConfigDriftResponse,
  ConfigPlanchado,
  ConfigPlancharAplicado,
  ConfigRecosechaResponse,
  ConfigSiteList,
  PlanchadoFlotaRespuesta,
  RecosechaEstado,
  RevertFieldRequest,
  RevertFieldResponse,
} from "@/api/types";

function buildDevicesQuery(filters: ConfigDeviceFilters): string {
  const params = new URLSearchParams();
  if (filters.site_id) params.set("site_id", filters.site_id);
  if (filters.device_type) params.set("device_type", filters.device_type);
  if (filters.client) params.set("client", filters.client);
  if (filters.status) params.set("status", filters.status);
  if (filters.page) params.set("page", String(filters.page));
  if (filters.per_page) params.set("per_page", String(filters.per_page));
  return params.toString();
}

export function useConfigSites(clientFilter?: string) {
  return useQuery({
    queryKey: ["config", "sites", clientFilter ?? null],
    queryFn: ({ signal }) => {
      const q = clientFilter ? `?client=${encodeURIComponent(clientFilter)}` : "";
      return api<ConfigSiteList>(`/api/v1/config/sites${q}`, { signal });
    },
    staleTime: 60_000,
  });
}

export function useConfigDevices(filters: ConfigDeviceFilters = {}) {
  const qs = buildDevicesQuery(filters);
  return useQuery({
    queryKey: ["config", "devices", filters],
    queryFn: ({ signal }) =>
      api<ConfigDeviceList>(
        `/api/v1/config/devices${qs ? `?${qs}` : ""}`,
        { signal },
      ),
    staleTime: 15_000,
    placeholderData: (prev) => prev,
  });
}

export function useConfigDevice(deviceId: string | null) {
  return useQuery({
    queryKey: ["config", "device", deviceId],
    queryFn: ({ signal }) =>
      api<ConfigDevice>(`/api/v1/config/devices/${deviceId}`, { signal }),
    enabled: !!deviceId,
  });
}

export function useConfigDeviceConfigs(
  deviceId: string | null,
  filters: { page?: number; per_page?: number } = {},
) {
  return useQuery({
    queryKey: ["config", "device-configs", deviceId, filters],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams();
      if (filters.page) params.set("page", String(filters.page));
      if (filters.per_page) params.set("per_page", String(filters.per_page));
      const qs = params.toString();
      return api<ConfigDeviceConfigList>(
        `/api/v1/config/devices/${deviceId}/configs${qs ? `?${qs}` : ""}`,
        { signal },
      );
    },
    enabled: !!deviceId,
    placeholderData: (prev) => prev,
  });
}

export function useConfigDeviceDiff(
  deviceId: string | null,
  fromId?: string | null,
  toId?: string | null,
) {
  return useQuery({
    queryKey: ["config", "device-diff", deviceId, fromId, toId],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams();
      if (fromId) params.set("from", fromId);
      if (toId) params.set("to", toId);
      const qs = params.toString();
      return api<ConfigDiffResponse>(
        `/api/v1/config/devices/${deviceId}/diff${qs ? `?${qs}` : ""}`,
        { signal },
      );
    },
    enabled: !!deviceId,
  });
}

export function useTakeSnapshot() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (deviceId: string) =>
      api<ConfigDeviceConfig>(
        `/api/v1/config/devices/${deviceId}/snapshot`,
        { method: "POST" },
      ),
    onSuccess: (_data, deviceId) => {
      void queryClient.invalidateQueries({
        queryKey: ["config", "device-configs", deviceId],
      });
      void queryClient.invalidateQueries({
        queryKey: ["config", "device-diff", deviceId],
      });
      void queryClient.invalidateQueries({
        queryKey: ["config", "devices"],
      });
      // La tabla de deriva se calcula en vivo desde el ultimo snapshot: al
      // recosechar un equipo, su fila debe recalcularse.
      void queryClient.invalidateQueries({ queryKey: ["config", "drift"] });
    },
  });
}

export function useProfileDevices(profileId: string | null) {
  return useQuery({
    queryKey: ["config", "profile-devices", profileId],
    queryFn: ({ signal }) =>
      api<ConfigDeviceList>(
        `/api/v1/config/profiles/${profileId}/devices`,
        { signal },
      ),
    enabled: !!profileId,
    staleTime: 30_000,
  });
}

export function useMarkGood() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      deviceId,
      configId,
    }: {
      deviceId: string;
      configId: string;
    }) =>
      api<ConfigDeviceConfig>(
        `/api/v1/config/devices/${deviceId}/mark-good?config_id=${configId}`,
        { method: "POST" },
      ),
    onSuccess: (_data, { deviceId }) => {
      void queryClient.invalidateQueries({
        queryKey: ["config", "device-configs", deviceId],
      });
      void queryClient.invalidateQueries({
        queryKey: ["config", "device-diff", deviceId],
      });
    },
  });
}

// ── Control de Configuración — drift accionable ──────────────────────────────

function buildDriftQuery(filters: ConfigDriftFilters): string {
  const params = new URLSearchParams();
  if (filters.scope) params.set("scope", filters.scope);
  if (filters.field) params.set("field", filters.field);
  if (filters.site) params.set("site", filters.site);
  if (filters.severity) params.set("severity", filters.severity);
  if (filters.device_type) params.set("device_type", filters.device_type);
  return params.toString();
}

export function useConfigDrift(
  filters: ConfigDriftFilters = {},
  // La pantalla comparte selector con HikCentral, que NO es deriva de config y
  // no tiene motor en /config/drift. Sin esto se dispararia una consulta que el
  // backend resolveria con el motor equivocado.
  enabled = true,
) {
  const qs = buildDriftQuery(filters);
  return useQuery({
    enabled,
    queryKey: ["config", "drift", filters],
    queryFn: ({ signal }) =>
      api<ConfigDriftResponse>(
        `/api/v1/config/drift${qs ? `?${qs}` : ""}`,
        { signal },
      ),
    staleTime: 15_000,
    placeholderData: (prev) => prev,
  });
}

// Revierte un campo (p.ej. time.timezone) en uno o varios equipos. Reusa el
// motor de rollback del backend; encola una entrada por equipo.
export function useRevertDriftField() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (req: RevertFieldRequest) =>
      api<RevertFieldResponse>(`/api/v1/config/drift/revert`, {
        method: "POST",
        body: req,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["config", "drift"] });
      void queryClient.invalidateQueries({ queryKey: ["rollbacks"] });
    },
  });
}

/**
 * Encola una recosecha en segundo plano de toda la flota de un device_type.
 * El backend responde de inmediato; la tabla se refresca sola con el refetch.
 */
export function useRecosechar() {
  return useMutation({
    mutationFn: (deviceType: string) =>
      api<ConfigRecosechaResponse>(
        `/api/v1/config/recosechar?device_type=${encodeURIComponent(deviceType)}`,
        { method: "POST" },
      ),
  });
}

/**
 * Avance en vivo de la recosecha: por donde va y que esta bajando ahora.
 *
 * Sondea mientras `activo`. Es lo que gobierna el aviso de la pantalla: antes
 * lo apagaba un temporizador ciego de 6 minutos en el cliente, que no tenia
 * forma de saber si el servidor habia terminado.
 */
export function useRecosechaEstado(deviceType: string, sondeando: boolean) {
  return useQuery({
    // Habilitada SIEMPRE, no solo mientras se sondea, por dos razones: al
    // entrar a la pantalla delata una barrida lanzada desde otra pestana o por
    // otro operador, y al terminar conserva el resultado en pantalla en vez de
    // borrarlo justo cuando hay algo que leer.
    enabled: true,
    queryKey: ["config", "recosecha-estado", deviceType],
    queryFn: ({ signal }) =>
      api<RecosechaEstado>(
        `/api/v1/config/recosechar/estado?device_type=${encodeURIComponent(deviceType)}`,
        { signal },
      ),
    // 2 s SOLO mientras corre: la barrida va de a 5 equipos y uno caido quema
    // su timeout, asi que hay algo nuevo que contar con esa frecuencia. En
    // reposo no sondea nada.
    refetchInterval: sondeando ? 2000 : false,
  });
}

/**
 * Aplica el planchado EJECUTANDO los comandos uci en el router (POST). Escribe
 * al equipo del cliente: el backend lo gatea con RUT_PLANCHADO_APPLY + admin.
 */
export function usePlancharAplicar() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (deviceId: string) =>
      api<ConfigPlancharAplicado>(
        `/api/v1/config/devices/${deviceId}/planchar/aplicar`,
        { method: "POST" },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["config", "drift"] });
    },
  });
}

/**
 * Plan EN SECO del planchado de flota. No abre una sola conexion a los
 * equipos: todo sale de las fotos ya capturadas, asi que se puede consultar
 * sin miedo y tantas veces como haga falta.
 *
 * `enabled` a peticion: no se pide al abrir la pantalla, solo cuando alguien
 * pulsa "ver que se plancharia".
 */
export function usePlanchadoFlotaPlan(activo: boolean) {
  return useQuery({
    queryKey: ["config", "planchar-flota", "plan"],
    enabled: activo,
    queryFn: ({ signal }) =>
      api<PlanchadoFlotaRespuesta>(`/api/v1/config/planchar-flota`, {
        method: "POST",
        signal,
      }),
    // Corto a proposito: entre ver el plan y aplicarlo puede haber pasado una
    // recosecha, y planchar contra un plan viejo escribe lo que ya no toca.
    staleTime: 30_000,
  });
}

/**
 * Aplica el planchado a TODA la flota. Escribe en los equipos del cliente: el
 * backend solo toca las familias cuyo candado esté abierto y devuelve cuántos
 * dejó fuera por ese motivo.
 */
export function usePlancharFlota() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<PlanchadoFlotaRespuesta>(
        `/api/v1/config/planchar-flota?aplicar=true`,
        { method: "POST" },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["config", "drift"] });
      void queryClient.invalidateQueries({ queryKey: ["config", "planchar-flota"] });
    },
  });
}

/** Comandos de planchado a golden de un RUT. Lazy: solo consulta con deviceId. */
export function usePlanchado(deviceId: string | null) {
  return useQuery({
    queryKey: ["config", "planchado", deviceId],
    enabled: Boolean(deviceId),
    queryFn: ({ signal }) =>
      api<ConfigPlanchado>(
        `/api/v1/config/devices/${deviceId}/planchado`,
        { signal },
      ),
    staleTime: 15_000,
  });
}

// Inventario y estado de la plataforma HikCentral. Cadencia baja a proposito:
// la vista se arma de los snapshots ya guardados, no habla con el VMS.
export function useHikCentral() {
  return useQuery({
    queryKey: ["config", "hikcentral"],
    queryFn: ({ signal }) =>
      api<HikCentralResponse>(`/api/v1/config/hikcentral`, { signal }),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}

// Camaras de HikCentral: inventario, plan de grabacion y estado con la causa
// ya atribuida. Se arma de snapshots ya guardados; no habla con la plataforma.
export function useCamaras() {
  return useQuery({
    queryKey: ["config", "camaras"],
    queryFn: ({ signal }) =>
      api<CamarasResponse>(`/api/v1/config/camaras`, { signal }),
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}

// Grabadores de Holanda: inventario, configuración y canales. Sale de los
// snapshots ISAPI ya guardados; no habla con los equipos.
export function useGrabadores() {
  return useQuery({
    queryKey: ["config", "grabadores"],
    queryFn: ({ signal }) =>
      api<GrabadoresResponse>(`/api/v1/config/grabadores`, { signal }),
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}

// Compat-Axelia de los grabadores: la codificación que cada NVR guarda por
// canal. El grabador se la reimpone a la cámara, así que aquí es donde se arregla.
export function useGrabadoresAxelia() {
  return useQuery({
    queryKey: ["config", "grabadores", "compat-axelia"],
    queryFn: ({ signal }) =>
      api<GrabadoresAxeliaResponse>(`/api/v1/config/grabadores/compat-axelia`, { signal }),
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}

// Plancha la plantilla en UN canal de un grabador (escribe en el NVR; el
// backend lo gatea con CAMARA_PLANCHADO_APPLY + admin).
export function usePlancharCanalGrabador() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ deviceId, canal }: { deviceId: string; canal: string }) =>
      api<ConfigPlancharAplicado>(
        `/api/v1/config/devices/${deviceId}/canales/${canal}/planchar/aplicar`,
        { method: "POST" },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["config", "grabadores"] });
    },
  });
}

// Resumen de cumplimiento de los biométricos (cobertura, cumplen, etc.). Es
// solo la cabecera: la deriva campo a campo sigue viniendo de /drift.
export function useBiometricos(enabled = true) {
  return useQuery({
    queryKey: ["config", "biometricos"],
    queryFn: ({ signal }) =>
      api<BiometricosResponse>(`/api/v1/config/biometricos`, { signal }),
    staleTime: 60_000,
    placeholderData: (prev) => prev,
    enabled,
  });
}

// Paneles de control de acceso: inventario, configuración y puertas.
export function usePaneles() {
  return useQuery({
    queryKey: ["config", "paneles"],
    queryFn: ({ signal }) =>
      api<PanelesResponse>(`/api/v1/config/paneles`, { signal }),
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}

// Golden de configuración de las cámaras y cumplimiento por agencia. Sale de
// los snapshots ISAPI ya guardados; no habla con las cámaras.
export function useCamarasConfig() {
  return useQuery({
    queryKey: ["config", "camaras", "configuracion"],
    queryFn: ({ signal }) =>
      api<CamarasConfigResponse>(`/api/v1/config/camaras/configuracion`, { signal }),
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}

// Inventario de la LAN por agencia. Se arma de snapshots ya guardados; no
// habla con los routers.
export function useInventarioLan() {
  return useQuery({
    queryKey: ["config", "inventario-lan"],
    queryFn: ({ signal }) =>
      api<InventarioLanResponse>(`/api/v1/config/inventario-lan`, { signal }),
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}
